import React from 'react';
import { Calendar as BigCalendar, Views, SlotInfo, stringOrDate } from 'react-big-calendar';
import withDragAndDrop, { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import { classNames } from 'primereact/utils';
import { Skeleton } from 'primereact/skeleton';
import { FormField } from '../../FormField/FormField';
import { FormFieldMessage } from '../../ConditionalFieldMessage/ConditionalFieldMessage';
import { calendarLocalizer, calendarMessages } from '../../../utils/calendarUtils';
import { CalendarRestrictions } from '../../../pages/Reservations/Reservations';
import { ReservationType } from '../../../types/reservationType';
import { ReservationEvent, ReservationFormFieldProps } from '../types';
import { findNearestAvailableSlot, isSlotAvailable } from '../utils/slots';
import { getHourDisplay } from '../utils/display';

const DragAndDropCalendar = withDragAndDrop<ReservationEvent, object>(BigCalendar);

interface Props extends Omit<ReservationFormFieldProps, 'setFormFields'> {
  canModifyDate: boolean;
  canSelectHour: boolean;
  showRequiredFields: boolean;
  isTouchDevice: boolean;
  isLoading: boolean;
  reservationType: ReservationType | undefined;
  calendarRestrictions: CalendarRestrictions;
  scrollTime: Date;
  /** The other reservations of the day. The new one cannot overlap them. */
  blockedEvents: ReservationEvent[];
  temporaryEvent: ReservationEvent | null;
  initialTemporaryEvent: ReservationEvent | undefined;
  invalidHourSelection: boolean;
  newEventTitle: string;
  /** Called with the event the user drew, moved or resized. */
  onTemporaryEventChange: (event: ReservationEvent) => void;
  onHoursChange: (start: Date, end: Date) => void;
}

/** A day calendar where the user selects the start and end hour of the reservation. */
export const HourPickerField = ({
  form,
  validationErrors,
  canModifyDate,
  canSelectHour,
  showRequiredFields,
  isTouchDevice,
  isLoading,
  reservationType,
  calendarRestrictions,
  scrollTime,
  blockedEvents,
  temporaryEvent,
  initialTemporaryEvent,
  invalidHourSelection,
  newEventTitle,
  onTemporaryEventChange,
  onHoursChange,
}: Props) => {
  const selectEvent = (newEvent: ReservationEvent) => {
    if (!isSlotAvailable(newEvent.start, newEvent.end, blockedEvents)) {
      return;
    }

    onTemporaryEventChange(newEvent);
    onHoursChange(newEvent.start, newEvent.end);
  };

  // Si es un click, buscamos el slot más cercano disponible
  const selectNearestSlot = (clickedTime: Date) => {
    const slot = findNearestAvailableSlot(
      clickedTime,
      blockedEvents,
      calendarRestrictions.maxTime,
      reservationType?.startsAtBegginingOfHour,
    );
    if (!slot) return;

    // Si la duración es fija, verificar que el slot tiene exactamente 1 hora
    if (reservationType?.fixedDuration) {
      const duration = (slot.end.getTime() - slot.start.getTime()) / (1000 * 60); // duración en minutos
      if (duration < 60) return; // Si el slot es más corto de 1 hora, no permitimos crear el evento
    }

    selectEvent({ id: 'temporary', title: newEventTitle, start: slot.start, end: slot.end });
  };

  const handleSelectSlot = (slotInfo: SlotInfo) => {
    // Si es un arrastre y la duración es fija, no permitimos crear el evento (en táctil se trata como un click)
    if (slotInfo.action === 'select' && reservationType?.fixedDuration) {
      if (!isTouchDevice) return;
      selectNearestSlot(slotInfo.start);
      return;
    }
    // Si es un arrastre y la duración no es fija, usamos el rango seleccionado
    if (slotInfo.action === 'select' && !reservationType?.fixedDuration) {
      const start = new Date(slotInfo.start);
      start.setMilliseconds(0);
      const end = new Date(slotInfo.end);
      end.setMilliseconds(0);

      selectEvent({ id: 'temporary', title: newEventTitle, start, end });
      return;
    }

    selectNearestSlot(slotInfo.start);
  };

  const moveEvent = (event: ReservationEvent, start: stringOrDate, end: stringOrDate) => {
    if (start instanceof Date && end instanceof Date) {
      if (!isSlotAvailable(start, end, blockedEvents)) {
        return;
      }

      if (event.id === 'temporary') {
        onTemporaryEventChange({
          ...event,
          start,
          end,
        });
      }
      onHoursChange(start, end);
    }
  };

  return (
    <FormField
      labelTitle='Hora'
      elementId='reservation-hours'
      fullWidth
      error={
        validationErrors?.localStartDate ||
        validationErrors?.localEndDate ||
        (invalidHourSelection ? 'La hora seleccionada no está disponible' : undefined)
      }
      required={showRequiredFields}
      className='reservation-hour-container'
    >
      {isTouchDevice && <span className='reservation-hour-container-touch-hint'>Pulsa y arrastra para seleccionar la hora</span>}
      {canModifyDate ? (
        canSelectHour ? (
          <div
            className={classNames('daily-calendar-container', { 'daily-calendar-container-touch': isTouchDevice })}
            id='reservation-hours'
          >
            {isLoading ? (
              <Skeleton height='100%' />
            ) : (
              <DragAndDropCalendar
                localizer={calendarLocalizer}
                date={form?.selectedDay}
                scrollToTime={scrollTime}
                onNavigate={() => {}}
                defaultView={Views.DAY}
                views={[Views.DAY]}
                events={[
                  ...(blockedEvents ?? []),
                  ...(temporaryEvent ? [temporaryEvent] : []),
                  ...(initialTemporaryEvent && !temporaryEvent ? [initialTemporaryEvent] : []),
                ]}
                step={5}
                timeslots={isTouchDevice ? 3 : 6}
                min={calendarRestrictions.minTime}
                max={calendarRestrictions.maxTime}
                selectable
                toolbar={false}
                eventPropGetter={(event: ReservationEvent) => ({
                  className: classNames({
                    'blocked-event': event.isBlocked,
                    'temporary-event': event.id === 'temporary',
                  }),
                })}
                onSelecting={() => {
                  return !reservationType?.fixedDuration || isTouchDevice;
                }}
                longPressThreshold={isTouchDevice ? 100 : undefined}
                onSelectSlot={handleSelectSlot}
                resizable={!isTouchDevice && !reservationType?.fixedDuration}
                onEventResize={({ event, start, end }: EventInteractionArgs<ReservationEvent>) => {
                  if (isTouchDevice || event.isBlocked || reservationType?.fixedDuration) return;
                  moveEvent(event, start, end);
                }}
                onEventDrop={({ event, start, end }: EventInteractionArgs<ReservationEvent>) => {
                  if (isTouchDevice || event.isBlocked) return;
                  moveEvent(event, start, end);
                }}
                className={classNames('daily-calendar', {
                  'p-invalid': !!validationErrors?.localStartDate || !!validationErrors?.localEndDate || invalidHourSelection,
                  'invalid-selection': invalidHourSelection,
                })}
                messages={calendarMessages}
              />
            )}
          </div>
        ) : (
          <FormFieldMessage message='Elige empleado y fecha' />
        )
      ) : (
        getHourDisplay(form?.localStartDate, form?.localEndDate)
      )}
    </FormField>
  );
};
